<script setup lang="ts">
import {
  useSeoBase,
  useJsonLd,
  orgNode,
  localBusinessNode,
} from "../../composables/useSeo";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";

// Access Nuxt app to get provided firebase instances
const { $db } = useNuxtApp();

useSeoBase({
  title: "Blog | Junk Removal Tips & News | Junk Guys MN",
  description:
    "Read our latest blog posts about junk removal tips, organization advice, and industry news.",
  canonical: "https://junkguysmn.com/blog",
});

useJsonLd([orgNode(), localBusinessNode()]);

// Newsletter functionality
const newsletterEmail = ref("");
const isNewsletterSubmitting = ref(false);
const newsletterMessage = ref("");
const newsletterStatus = ref<"success" | "error" | "">("");

const handleNewsletterSubmit = async (event: Event) => {
  event.preventDefault();

  if (!newsletterEmail.value) return;

  isNewsletterSubmitting.value = true;
  newsletterMessage.value = "";
  newsletterStatus.value = "";

  try {
    await addDoc(collection($db, "subscribers"), {
      email: newsletterEmail.value,
      subscribedAt: serverTimestamp(),
      source: "blog_footer",
    });

    newsletterStatus.value = "success";
    newsletterMessage.value = "Successfully subscribed to our newsletter!";
    newsletterEmail.value = "";
  } catch (error: any) {
    console.error("Newsletter Subscription Error:", error);
    newsletterStatus.value = "error";
    newsletterMessage.value = "Failed to subscribe. Please try again.";
  } finally {
    isNewsletterSubmitting.value = false;
  }
};
</script>

<template>
  <main class="container mx-auto px-4 py-8">
    <div class="text-center mb-8">
      <h1 class="text-4xl font-bold">Junk Removal Blog</h1>
    </div>
    <p class="text-center text-lg mb-12">
      Tips, advice, and news about junk removal and organization
    </p>

    <div class="max-w-6xl mx-auto">
      <!-- Featured Post -->
      <section class="mb-16">
        <h2 class="text-2xl font-semibold mb-6">Featured Post</h2>
        <div class="bg-white rounded-lg shadow-md overflow-hidden">
          <div class="md:flex">
            <div class="md:w-1/3">
              <div class="h-64 bg-gray-200 flex items-center justify-center">
                <span class="text-gray-500">Featured Image</span>
              </div>
            </div>
            <div class="md:w-2/3 p-8">
              <div class="text-sm text-primary-main mb-2">
                Organization Tips
              </div>
              <h3 class="text-2xl font-semibold mb-4">
                10 Essential Steps for a Successful Garage Cleanout
              </h3>
              <p class="text-gray-600 mb-4">
                A garage cleanout can seem overwhelming, but with the right
                approach, you can transform your cluttered space into an
                organized, functional area. Here are our top 10 steps to make
                your garage cleanout a success.
              </p>
              <div class="flex items-center justify-between">
                <span class="text-sm text-gray-500">March 15, 2024</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Blog Grid -->
      <section class="mb-16">
        <h2 class="text-2xl font-semibold mb-6">Latest Posts</h2>
        <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          <!-- Post 1 -->
          <article class="bg-white rounded-lg shadow-md overflow-hidden">
            <div class="h-48 bg-gray-200 flex items-center justify-center">
              <span class="text-gray-500">Blog Image</span>
            </div>
            <div class="p-6">
              <div class="text-sm text-primary-main mb-2">Pricing Guide</div>
              <h3 class="text-lg font-semibold mb-3">
                Understanding Junk Removal Pricing: What You Need to Know
              </h3>
              <p class="text-gray-600 mb-4">
                Learn how junk removal companies determine pricing and what
                factors affect the cost of your service.
              </p>
              <div class="flex items-center justify-between">
                <span class="text-sm text-gray-500">March 10, 2024</span>
              </div>
            </div>
          </article>

          <!-- Post 2 -->
          <article class="bg-white rounded-lg shadow-md overflow-hidden">
            <div class="h-48 bg-gray-200 flex items-center justify-center">
              <span class="text-gray-500">Blog Image</span>
            </div>
            <div class="p-6">
              <div class="text-sm text-primary-main mb-2">Eco-Friendly</div>
              <h3 class="text-lg font-semibold mb-3">
                Sustainable Junk Removal: How We're Going Green
              </h3>
              <p class="text-gray-600 mb-4">
                Discover our commitment to environmental responsibility and how
                we minimize waste through recycling and donation programs.
              </p>
              <div class="flex items-center justify-between">
                <span class="text-sm text-gray-500">March 5, 2024</span>
              </div>
            </div>
          </article>

          <!-- Post 3 -->
          <article class="bg-white rounded-lg shadow-md overflow-hidden">
            <div class="h-48 bg-gray-200 flex items-center justify-center">
              <span class="text-gray-500">Blog Image</span>
            </div>
            <div class="p-6">
              <div class="text-sm text-primary-main mb-2">DIY Tips</div>
              <h3 class="text-lg font-semibold mb-3">
                Preparing for Junk Removal: A Complete Checklist
              </h3>
              <p class="text-gray-600 mb-4">
                Make the most of your junk removal service with our
                comprehensive preparation checklist.
              </p>
              <div class="flex items-center justify-between">
                <span class="text-sm text-gray-500">February 28, 2024</span>
              </div>
            </div>
          </article>

          <!-- Post 4 -->
          <article class="bg-white rounded-lg shadow-md overflow-hidden">
            <div class="h-48 bg-gray-200 flex items-center justify-center">
              <span class="text-gray-500">Blog Image</span>
            </div>
            <div class="p-6">
              <div class="text-sm text-primary-main mb-2">Service Guide</div>
              <h3 class="text-lg font-semibold mb-3">
                When to Choose Professional Junk Removal vs. DIY
              </h3>
              <p class="text-gray-600 mb-4">
                Not sure whether to tackle junk removal yourself or hire
                professionals? We'll help you decide.
              </p>
              <div class="flex items-center justify-between">
                <span class="text-sm text-gray-500">February 20, 2024</span>
              </div>
            </div>
          </article>

          <!-- Post 5 -->
          <article class="bg-white rounded-lg shadow-md overflow-hidden">
            <div class="h-48 bg-gray-200 flex items-center justify-center">
              <span class="text-gray-500">Blog Image</span>
            </div>
            <div class="p-6">
              <div class="text-sm text-primary-main mb-2">Industry News</div>
              <h3 class="text-lg font-semibold mb-3">
                The Future of Junk Removal: Technology and Innovation
              </h3>
              <p class="text-gray-600 mb-4">
                Explore how technology is transforming the junk removal industry
                and improving customer experience.
              </p>
              <div class="flex items-center justify-between">
                <span class="text-sm text-gray-500">February 15, 2024</span>
              </div>
            </div>
          </article>

          <!-- Post 6 -->
          <article class="bg-white rounded-lg shadow-md overflow-hidden">
            <div class="h-48 bg-gray-200 flex items-center justify-center">
              <span class="text-gray-500">Blog Image</span>
            </div>
            <div class="p-6">
              <div class="text-sm text-primary-main mb-2">Customer Stories</div>
              <h3 class="text-lg font-semibold mb-3">
                Customer Spotlight: The Johnson Family's Basement Transformation
              </h3>
              <p class="text-gray-600 mb-4">
                Read about how we helped the Johnson family transform their
                cluttered basement into a functional living space.
              </p>
              <div class="flex items-center justify-between">
                <span class="text-sm text-gray-500">February 10, 2024</span>
              </div>
            </div>
          </article>
        </div>
      </section>

      <!-- Newsletter Signup -->
      <section class="text-center bg-primary-main/5 rounded-lg p-8">
        <h2 class="text-2xl font-semibold mb-4">Stay Updated</h2>
        <p class="text-lg mb-6">
          Get the latest junk removal tips and news delivered to your inbox.
        </p>
        <div class="max-w-md mx-auto">
          <form @submit="handleNewsletterSubmit" class="space-y-4">
            <div class="flex gap-2">
              <input
                v-model="newsletterEmail"
                type="email"
                placeholder="Enter your email"
                required
                class="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-main"
              />
              <button
                type="submit"
                :disabled="isNewsletterSubmitting"
                class="bg-primary-main text-white px-6 py-2 rounded-md hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span v-if="isNewsletterSubmitting">Subscribing...</span>
                <span v-else>Subscribe</span>
              </button>
            </div>

            <!-- Newsletter Message -->
            <div
              v-if="newsletterMessage"
              class="text-sm p-3 rounded-md"
              :class="
                newsletterStatus === 'success'
                  ? 'bg-green-50 text-green-800 border border-green-200'
                  : 'bg-red-50 text-red-800 border border-red-200'
              "
            >
              {{ newsletterMessage }}
            </div>
          </form>
        </div>
      </section>
    </div>
  </main>
</template>
